import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import {
  Loader2, ChevronLeft, ChevronRight, Inbox, Trash2, Archive,
  Mail, MailOpen, RefreshCw,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import EmailListItem from "@/components/mail/EmailListItem";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getHeaders = () => {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export default function MailInbox() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [emails, setEmails] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(new Set());
  const [page, setPage] = useState(1);
  const limit = 50;

  // Determine folder from path
  const path = window.location.pathname;
  const folder = path.split("/").pop() || "inbox";
  const searchQuery = searchParams.get("search") || "";

  useEffect(() => {
    fetchEmails();
  }, [folder, page, searchQuery]);

  const fetchEmails = async () => {
    setLoading(true);
    try {
      const params = { folder, page, limit };
      if (searchQuery) params.search = searchQuery;

      const res = await axios.get(`${API}/mail/emails`, {
        params, headers: getHeaders(), withCredentials: true,
      });
      setEmails(res.data.emails || []);
      setTotal(res.data.total || 0);
    } catch (err) {
      toast.error("Failed to load emails");
    }
    setLoading(false);
  };

  const toggleStar = async (emailId) => {
    try {
      await axios.post(`${API}/mail/emails/${emailId}/star`, {}, {
        headers: getHeaders(), withCredentials: true,
      });
      fetchEmails();
    } catch {}
  };

  const toggleFlag = async (emailId) => {
    try {
      await axios.post(`${API}/mail/emails/${emailId}/flag`, {}, {
        headers: getHeaders(), withCredentials: true,
      });
      fetchEmails();
    } catch {}
  };

  const handleSelect = (emailId) => {
    const next = new Set(selected);
    if (next.has(emailId)) next.delete(emailId);
    else next.add(emailId);
    setSelected(next);
  };

  const handleSelectAll = () => {
    if (selected.size === emails.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(emails.map((e) => e.id)));
    }
  };

  const handleBulkDelete = async () => {
    for (const id of selected) {
      await axios.delete(`${API}/mail/emails/${id}`, {
        headers: getHeaders(), withCredentials: true,
      });
    }
    setSelected(new Set());
    fetchEmails();
    toast.success("Emails moved to trash");
  };

  const handleBulkArchive = async () => {
    for (const id of selected) {
      await axios.post(`${API}/mail/emails/${id}/move`, { target_folder: "archive" }, {
        headers: getHeaders(), withCredentials: true,
      });
    }
    setSelected(new Set());
    fetchEmails();
    toast.success("Emails archived");
  };

  const handleBulkRead = async (isRead) => {
    for (const id of selected) {
      await axios.patch(`${API}/mail/emails/${id}/read?is_read=${isRead}`, {}, {
        headers: getHeaders(), withCredentials: true,
      });
    }
    setSelected(new Set());
    fetchEmails();
  };

  const totalPages = Math.ceil(total / limit);
  const folderLabel = folder.charAt(0).toUpperCase() + folder.slice(1);

  return (
    <div className="flex flex-col h-full">
      {/* Header bar */}
      <div className="flex items-center justify-between px-4 py-2 border-b border-app bg-app-card">
        <div className="flex items-center gap-3">
          <h2 className="text-sm font-semibold text-app">{folderLabel}</h2>
          <span className="text-[10px] text-app-secondary">{total} messages</span>
          <Button variant="ghost" size="sm" onClick={fetchEmails} className="h-6 w-6 p-0">
            <RefreshCw className="w-3 h-3 text-app-secondary" />
          </Button>
        </div>

        {selected.size > 0 && (
          <div className="flex items-center gap-1">
            <span className="text-[10px] text-app-secondary mr-1">{selected.size} selected</span>
            <Button variant="ghost" size="sm" onClick={() => handleBulkRead(true)} className="h-6 text-[10px] px-2">
              <MailOpen className="w-3 h-3 mr-1" /> Read
            </Button>
            <Button variant="ghost" size="sm" onClick={() => handleBulkRead(false)} className="h-6 text-[10px] px-2">
              <Mail className="w-3 h-3 mr-1" /> Unread
            </Button>
            <Button variant="ghost" size="sm" onClick={handleBulkArchive} className="h-6 text-[10px] px-2">
              <Archive className="w-3 h-3 mr-1" /> Archive
            </Button>
            <Button variant="ghost" size="sm" onClick={handleBulkDelete} className="h-6 text-[10px] px-2 text-red-500">
              <Trash2 className="w-3 h-3 mr-1" /> Delete
            </Button>
          </div>
        )}
      </div>

      {/* Column headers */}
      <div className="flex items-center gap-2 px-3 py-1.5 bg-app border-b border-app text-[10px] font-medium text-app-secondary">
        <input
          type="checkbox"
          checked={emails.length > 0 && selected.size === emails.length}
          onChange={handleSelectAll}
          className="w-3.5 h-3.5 rounded border-app text-violet-600"
        />
        <span className="w-[22px]" /> {/* star */}
        <span className="w-[18px]" /> {/* flag */}
        <span className="w-28">Sender</span>
        <span className="flex-1">Subject</span>
        <span className="w-16 text-right">Date</span>
      </div>

      {/* Email list */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-6 h-6 animate-spin text-violet-500" />
        </div>
      ) : emails.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-12 text-app-secondary">
          <Inbox className="w-10 h-10 mb-2" />
          <p className="text-sm">No emails in {folderLabel}</p>
        </div>
      ) : (
        <div className="flex-1 overflow-auto">
          {emails.map((email) => (
            <EmailListItem
              key={email.id}
              email={email}
              isSelected={selected.has(email.id)}
              onSelect={() => handleSelect(email.id)}
              onClick={() => navigate(`/comms-centre/mail/detail/${email.id}`)}
              onStar={() => toggleStar(email.id)}
              onFlag={() => toggleFlag(email.id)}
            />
          ))}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between px-4 py-2 border-t border-app bg-app-card text-xs text-app-secondary">
          <span>{total} messages</span>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" className="h-6 w-6 p-0 rounded-none"
              disabled={page === 1} onClick={() => setPage(page - 1)}
            >
              <ChevronLeft className="w-3 h-3" />
            </Button>
            <span>Page {page} of {totalPages}</span>
            <Button variant="outline" size="sm" className="h-6 w-6 p-0 rounded-none"
              disabled={page >= totalPages} onClick={() => setPage(page + 1)}
            >
              <ChevronRight className="w-3 h-3" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
